import type { ApolloError } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import {
  GetCareerObjectivesDocument,
  UpdateCareerObjectivesDocument,
} from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  updateSummary as updateSummaryAction,
  updateSummaryFailure,
  updateSummarySuccess,
} from './actionCreators';
import {
  generateCareerObjectivesOptimisticResponse,
  updateCacheUpdateCareerObjectives,
} from './apolloCache';

export const useSummary = () => {
  const { loading, error, data } = useQuery(GetCareerObjectivesDocument);

  const summary =
    loading || error
      ? ''
      : data?.viewer?.careerObjectives?.personalStatement || '';

  return { summary, loading, error };
};

const key = 'summary';

export const useUpdateSummary = () => {
  const { t } = useTranslations(translations);
  const { candidateId } = useCandidateId();
  const dispatch = useDispatch();

  const onCompleted = () => {
    dispatch(updateSummarySuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(updateSummaryFailure(error));
  };

  return useToastMutation(UpdateCareerObjectivesDocument, {
    key,
    errorActionRouteParameters: { card: 'summary', mode: 'READ' },
    onMutate: ({ careerObjectivesData: { personalStatement } }) => {
      dispatch(updateSummaryAction(personalStatement));
    },
    onCompleted,
    onError,
    successToastMessage: t('Summary updated'),
    update: updateCacheUpdateCareerObjectives,
    optimisticResponse: generateCareerObjectivesOptimisticResponse(
      candidateId || '',
    ),
  });
};
